<template>
    <div>
        <!-- ヘッダー -->
        <div class="mb-4">
            <h2 class="fw-bold text-primary mb-1">
                <i class="fas fa-receipt me-2"></i>領収書一覧
            </h2>
            <p class="text-muted mb-0">領収書情報の確認ができます。</p>
        </div>
        <div class="container" v-cloak>
            <!-- フィルター -->
            <div class="card shadow-sm mb-4">
                <!-- ヘッダー -->
                <div class="card-header bg-white border-bottom py-3">
                    <h6 class="mb-0 text-secondary fw-semibold">
                        <i class="fa-solid fa-filter me-2 text-primary"></i>検索条件
                    </h6>
                </div>

                <div class="card-body p-4">
                    <div class="row g-3">
                        <!-- 1行目：基本情報 -->
                        <div class="col-lg-4 col-md-6">
                            <label class="form-label text-muted small fw-semibold mb-2">
                                <i class="fa-solid fa-building me-1"></i>取引先
                            </label>
                            <dropdown-selector
                                v-model="filter.client"
                                @change="find()"
                                placeholder="取引先を選択"
                                label="label"
                                :options="clients"
                            ></dropdown-selector>
                        </div>

                        <div class="col-lg-4 col-md-6">
                            <label class="form-label text-muted small fw-semibold mb-2">
                                <i class="fa-solid fa-calendar-days me-1"></i>購入日
                            </label>
                            <range-picker
                                v-model="filter.purchase_date"
                                @change="find()"
                                placeholder="購入日を選択"
                                class="modern-range"
                            ></range-picker>
                        </div>

                        <div class="col-lg-4 col-md-6">
                            <label class="form-label text-muted small fw-semibold mb-2">
                                <i class="fa-solid fa-tag me-1"></i>タグ
                            </label>
                            <dropdown-selector
                                v-model="filter.tag"
                                @change="find()"
                                placeholder="タグを選択"
                                label="name"
                                :options="tags"
                            ></dropdown-selector>
                        </div>

                        <!-- 2行目：会計情報 -->
                        <div class="col-lg-4 col-md-6">
                            <label class="form-label text-muted small fw-semibold mb-2">
                                <i class="fa-solid fa-book me-1"></i>勘定科目
                            </label>
                            <dropdown-selector
                                v-model="filter.account_title"
                                @change="find()"
                                placeholder="勘定科目を選択"
                                label="label"
                                :options="account_titles"
                            ></dropdown-selector>
                        </div>

                        <div class="col-lg-4 col-md-6">
                            <label class="form-label text-muted small fw-semibold mb-2">
                                <i class="fa-solid fa-yen-sign me-1"></i>合計金額
                            </label>
                            <div class="input-group modern-range-input">
                                <input
                                    type="number"
                                    v-model.number="filter.total_amount_min"
                                    @change="find()"
                                    class="form-control"
                                    placeholder="最小金額"
                                    min="0"
                                />
                                <span class="input-group-text bg-white border-start-0 border-end-0 px-2">
                                    <i class="fa-solid fa-minus text-muted small"></i>
                                </span>
                                <input
                                    type="number"
                                    v-model.number="filter.total_amount_max"
                                    @change="find()"
                                    class="form-control"
                                    placeholder="最大金額"
                                    min="0"
                                />
                            </div>
                        </div>

                        <div class="col-lg-4 col-md-6">
                            <label class="form-label text-muted small fw-semibold mb-2">
                                <i class="fa-solid fa-percent me-1"></i>消費税率
                            </label>
                            <select v-model="filter.tax_rate" class="form-select modern-select" @change="find()">
                                <option value="">すべて</option>
                                <option :value="10">10%</option>
                                <option :value="8">8%（軽減税率）</option>
                                <option :value="0">0%（非課税）</option>
                            </select>
                        </div>

                        <!-- 3行目：詳細情報 -->
                        <div class="col-12">
                            <label class="form-label text-muted small fw-semibold mb-2">
                                <i class="fa-solid fa-align-left me-1"></i>
                                摘要
                            </label>
                            <input
                                type="text"
                                v-model="filter.description"
                                @change="find()"
                                class="form-control modern-input"
                                placeholder="摘要キーワードを入力"
                            />
                        </div>
                    </div>

                    <!-- アクションボタン -->
                    <div class="d-flex justify-content-end gap-2 mt-4 pt-3">
                        <button class="btn btn-outline-secondary px-4" @click="reset()">
                            <i class="fa-solid fa-rotate-left me-2"></i>リセット
                        </button>
                        <button class="btn btn-primary px-4 shadow-sm d-none" @click="find()">
                            <i class="fa-solid fa-magnifying-glass me-2"></i>検索
                        </button>
                    </div>
                </div>
            </div>

            <!-- ページネーション -->
            <div class="sticky mb-3">
                <pagination v-bind="receipts" @goto="goto" :showTotal="true"></pagination>
            </div>

            <div v-if="!receipts?.data" class="text-center text-muted py-5">
                領収書データがありません
            </div>
            <div v-else>
                <div v-for="receipt in receipts.data" :key="receipt.id" class="card mb-3" :class="{'opacity-50': receipt.deleted_at}">
                    <!-- header部分 -->
                    <div class="card-header d-flex justify-content-between align-items-center py-2 px-3">
                        <div class="d-flex align-items-center gap-2">
                            <span class="fw-bold fs-5 ms-2">
                                {{ receipt.client.client_name }}
                                ID: {{ receipt.id }}
                            </span>
                        </div>
                        <div class="d-flex align-items-center gap-3">
                            <!-- タグ -->
                            <div class="d-flex align-items-center gap-1">
                                <span class="text-muted small">タグ</span>
                                <span v-if="receipt.tag" class="badge bg-info text-white fs-6">
                                    {{ receipt.tag.name }}
                                </span>
                                <span v-else class="badge bg-light text-white fs-6">
                                    なし
                                </span>
                            </div>

                            <!-- 購入日 -->
                            <div class="d-flex align-items-center gap-1">
                                <span class="text-muted small">購入日</span>
                                <span class="badge bg-secondary fs-6">{{ receipt.purchase_date }}</span>
                            </div>

                        </div>
                    </div>

                    <!-- body部分 -->
                    <div class="card-body py-3">
                        <div class="row">
                            <!-- 画像エリア -->
                            <div class="col-md-3 mb-3 mb-md-0">
                                <div v-if="receipt.receipt_image && receipt.receipt_image.path" class="receipt-thumbnail-wrapper">
                                    <a :href="receipt.receipt_image.path"
                                        data-type="image"
                                        :data-title="receipt.receipt_image.file_name"
                                        :data-description="receipt.client?.client_name || '—'"
                                        data-zoomable="true"
                                        data-draggable="false"
                                        class="glightbox"
                                        v-if="!receipt.receipt_image.path.match(/\.pdf$/)">
                                        <img
                                            :src="receipt.receipt_image.path"
                                            alt="領収書サムネイル"
                                            class="receipt-thumbnail img-thumbnail w-100"
                                            style="cursor: pointer;"
                                        />
                                    </a>
                                    <preview-file :receipt_image="receipt.receipt_image" :receipt="receipt" v-else />
                                </div>
                                <div v-else class="text-center text-muted small p-3 border rounded">
                                    画像なし
                                </div>
                            </div>

                            <!-- 明細エリア -->
                            <div class="col-md-9">
                                <div class="border rounded p-3 mb-3">
                                    <div class="d-flex justify-content-between align-items-center mb-3">
                                        <h6 class="mb-0">
                                            <i class="fa-solid fa-list-ul me-2 text-secondary"></i>明細
                                        </h6>
                                        <span v-if="receipt.receipt_details.length > 0" class="fw-bold text-primary">
                                            合計金額: ¥ {{ total(receipt) }}
                                        </span>
                                    </div>

                                    <div v-if="receipt.receipt_details.length === 0" class="text-center text-muted py-3">
                                        明細はありません
                                    </div>

                                    <div v-else class="table-responsive">
                                        <table class="table table-sm table-hover mb-0">
                                            <thead class="table-light">
                                                <tr>
                                                    <th>勘定科目</th>
                                                    <th class="text-end">金額</th>
                                                    <th class="text-center">税率</th>
                                                    <th class="text-end">消費税</th>
                                                    <th>税区分</th>
                                                    <th>摘要</th>
                                                    <th>TKC</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="d in receipt.receipt_details" :key="d.id">
                                                    <td>{{ d.account_title.name }}</td>
                                                    <td class="text-end">¥{{ d.amount.toLocaleString() }}</td>
                                                    <td class="text-center">
                                                        <span class="badge bg-info">{{ d.tax_rate }}%</span>
                                                    </td>
                                                    <td class="text-end">¥{{ d.tax.toLocaleString() }}</td>
                                                    <td>{{ d.tax_category.name }}</td>
                                                    <td>{{ d.description || '—' }}</td>
                                                    <td>{{ d.tkc_voucher_number || '—' }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div class="border rounded p-3" v-if="receipt.receipt_images.length > 0">
                                    <h6 class="mb-3">
                                        <i class="fa-solid fa-list-ul me-2 text-secondary"></i>
                                        添付ファイル
                                    </h6>
                                    <div class="d-flex justify-content-start">
                                        <div v-for="v in receipt.receipt_images" :key="v.id" class="me-3">
                                            <preview-file :receipt_image="v" :receipt="receipt" :is_icon="true" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <loading
                v-model:active="isLoading"
                :can-cancel="false"
                :is-full-page="true"></loading>
        </div>
    </div>
</template>

<style scoped>
.card-header {
    background: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}
.card-header .btn {
    min-width: 2.2rem;
    min-height: 2.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.25rem 0.5rem;
}
.card-header .btn + .btn {
    margin-left: 0.5rem;
}
.card-body {
    padding-top: 0.75rem !important;
    padding-bottom: 0.75rem !important;
}
.detail-section {
    border: 1.5px solid #e0e0e0;
    border-radius: 0.6rem;
    background: #f8fafc;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    padding: 1rem 0.5rem 0.5rem 0.5rem;
}
.detail-title-inside {
    font-size: 1rem;
    color: #495057;
    font-weight: bold;
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;
}
.detail-row:not(:last-child) {
    border-bottom: 1px dashed #e0e0e0;
}
.detail-row {
    background: transparent;
}
.receipt-thumbnail-wrapper {
    cursor: pointer;
    width: 100%;
}

.receipt-thumbnail {
    width: 100%;
    height: auto;
    max-height: 120px;
    object-fit: contain;
}
/* カード全体 */
.card {
    border-radius: 12px;
    transition: box-shadow 0.3s ease;
}

.card:hover {
    box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.08) !important;
}

.card-header {
    border-radius: 12px 12px 0 0 !important;
}


/* 範囲入力 */
.modern-range-input .form-control {
    border-radius: 8px 0 0 8px;
}

.modern-range-input .form-control:last-child {
    border-radius: 0 8px 8px 0;
}

.modern-range-input .input-group-text {
    border: 1px solid #dee2e6;
    border-left: 0;
    border-right: 0;
}

.modern-range-input .form-control:focus + .input-group-text {
    border-color: #0d6efd;
}

/* ラベル */
.form-label {
    margin-bottom: 0.5rem;
    letter-spacing: 0.01em;
}

.form-label i {
    opacity: 0.7;
    font-size: 0.875rem;
}

/* レスポンシブ調整 */
@media (max-width: 768px) {
    .card-body {
        padding: 1.25rem !important;
    }

    .btn {
        width: 100%;
    }

    .d-flex.gap-2 {
        flex-direction: column-reverse;
    }
}

/* アクセシビリティ */
.form-control:focus-visible,
.form-select:focus-visible,
.btn:focus-visible {
    outline: 2px solid #0d6efd;
    outline-offset: 2px;
}

</style>

<script>
import dropdownSelector from 'components/dropdownSelector.vue';
import previewFile from 'components/previewFile.vue';
import GLightbox from 'glightbox';
export default {
    components: {
        dropdownSelector,
        previewFile,
    },
    data() {
        return {
            receipts: {},
            // マスタ
            account_titles: [],
            clients: [],
            partner_accounts: [],
            tags: [],
            tax_categories: [],
            modals: [],

            isLoading: false,
            page: 1,
            previewImage: '',
            filter: {
                client: null,
                tag: null,
                account_title: null,
                purchase_date: null,
                total_amount_min: '',
                total_amount_max: '',
                description: '',
                tax_rate: '',
            },
            receipt_image_id: null,
            partner_account: null,
            lightbox: null,
        }
    },
    methods: {
        init() {
            axios.get('/api/master/dropdowns').then(res => {
                this.account_titles = res.data.account_titles;
                this.clients = res.data.clients;
                this.partner_accounts = res.data.partner_accounts;
                this.tags = res.data.tags;
                this.tax_categories = res.data.tax_categories;
            }).catch(err => {Alert.err(err)});
        },
        initGlightbox() {
            this.lightbox = GLightbox({
                selector: '.glightbox',
                touchNavigation: true,
                loop: true,
                autoplayVideos: true
            });
        },
        reload() {
            this.isLoading = true;

            const params = this.filter;
            params.page = this.page;

            axios.get('/api/tools/receipts', { params: params }).then(res => {
                this.receipts = res.data;
                this.isLoading = false;
                $("html,body").animate({scrollTop: 0});
                setTimeout(() => {
                    this.initGlightbox();
                });
            }).catch(err => {
                Alert.err(err);
                this.isLoading = false;
            });
        },
        closeModal(id) {
            this.modals[id]?.hide();
        },
        goto(p) {
            this.page = p;
            this.reload();
        },
        find() {
            this.page = 1
            this.reload()
        },
        reset() {
            this.filter = {
                client: null,
                tag: null,
                account_title: null,
                purchase_date: null,
                total_amount_min: '',
                total_amount_max: '',
                description: '',
                tax_rate: '',
            }
            this.page = 1
            this.reload()
        },
        total(receipt) {
            let sum = 0;
            for (const detail of receipt.receipt_details) {
                sum += detail.amount;
            }
            return sum.toLocaleString();
        },
    },
    mounted() {
        this.init();
        this.reload()
    },
    beforeUnmount() {
        if (this.lightbox) {
            this.lightbox.destroy();
        }
    }
}
</script>
